// ────────────────────────────────────────────────────────────────
// help/help-content.ts — Phase A help pages (§6.2 #11, NFR-8)
// Markdown-driven content, one topic per module. Slugs double as the deep-link
// target from each screen's "?" (the shell maps the active route → slug).
// Keep this factual to what the app actually does — it is the operator manual.
// ────────────────────────────────────────────────────────────────

export interface HelpTopic {
  slug: string;
  title: string;
  icon: string;
  summary: string;
  markdown: string;
}

export const HELP_TOPICS: HelpTopic[] = [
  {
    slug: 'getting-started',
    title: 'Getting started',
    icon: 'pi pi-compass',
    summary: 'What MDTA is and how to find your way around.',
    markdown: `# Getting started

**MDTA** is the back-office console for the mydreamteam.ai platform. It is used only by the platform owner and trusted colleagues — it is **not** customer-facing, and it keeps running even when the main app is down or in maintenance.

## The layout

- The **left navigation** lists the areas you have permission to use: Exceptions, Health, Maintenance, Promotions, Audit, Settings, and Help.
- The **top bar** carries the **environment switcher** (Development / Testing / Production) and your account menu.
- The **?** in the top bar always opens the help page for the screen you are on.

## Everyday flow

1. Pick the environment you want to work in (top-right).
2. Start on **Exceptions** — it tells you what, if anything, needs your attention.
3. Use **Health** to confirm the platform's infrastructure and third-party services are up.
4. **Maintenance** and **Promotions** are where you make changes; every change you make is recorded in the **Audit** trail.

## A note on codes

You will never see a bare status number in MDTA. Every code is shown as **its plain-language label followed by the code in parentheses**, with a tooltip that explains what it means. Hover anything that looks like a status to read its definition.

See also: [Environments & production safety](/help/environments).`,
  },
  {
    slug: 'environments',
    title: 'Environments & production safety',
    icon: 'pi pi-server',
    summary: 'How dev/tst/prd work and the guardrails around production.',
    markdown: `# Environments & production safety

The platform runs in three environments. In MDTA the environment is a **runtime choice** — you switch it in the top bar; it is not a separate build or login.

- **Development (dev)** — safe to experiment in.
- **Testing (tst)** — pre-production verification.
- **Production (prd)** — **live customers**. Treat every change here as real.

## Switching environments

Changing the environment clears any in-progress screen state and is remembered for your account. The Health board is the one screen that shows **all** environments at once.

## Production is loud on purpose

When you select **Production**:

- A red **PRODUCTION** banner stays pinned to the top of the screen.
- The whole interface takes on a red accent so you always know where you are.

## The typed confirmation

Any change you make in production (a maintenance toggle, a coupon edit, and so on) requires you to **type \`prd\` to confirm** before it is sent. That confirmation arms the request for **exactly one** action — the next production change asks again.

The server enforces this independently: a production change that arrives without the confirmation is rejected. This is deliberate friction, not a bug.`,
  },
  {
    slug: 'health',
    title: 'Health board',
    icon: 'pi pi-heart-fill',
    summary: 'Reading the probes and running checks on demand.',
    markdown: `# Health board

The Health board shows the live status of everything the platform depends on. It is the only screen that displays **all environments at once**.

## Two sections

- **Infrastructure** — per-environment checks (database, and the queries / chat / speech / rag / security-function services), shown in columns for dev, tst, and prd.
- **Third-party (shared)** — external services used across all environments (OpenAI, Qdrant, Mailgun, Hive, Google Speech, Morning, Anthropic).

## Reading a card

Each card shows a **status dot**, the service label, its latest **latency**, when it was **last checked**, and a small **24-hour history** strip.

- **OK** — responded normally and quickly.
- **Degraded** — responded, but noticeably slower than its recent normal (more than three times the rolling median latency).
- **Down** — did not respond, timed out, or returned an error.

## Running checks

- **Run now** on a card re-checks that single service immediately.
- **Run all** in the header re-checks everything. Because probes run one after another, a full sweep can take a little while — that is expected.

## Automatic monitoring

You do not need to keep this screen open. Infrastructure probes run on a schedule (about every 5 minutes) and third-party probes hourly. When something goes down you receive an **email alert**, a reminder if it stays down, and a recovery notice when it comes back. Configure the recipients under [Settings](/help/settings).

## Security: docs-private ACL scan

A separate hourly check (prd only) scans every file under \`docs-private\` in the documents storage bucket and confirms it is not publicly readable. There is no unrecoverable failure state here: if a file is ever found public, it is reset to private **in the same run** — so the card only ever shows **Clean** (nothing found) or **Remediated** (something was found and already fixed). A remediation still sends an alert email, because a file becoming public means something upstream put it there, even though the exposure itself is already closed. The **recent remediation events** list below the card is there so a file that keeps flipping public shows up as a pattern rather than a string of one-off incidents.`,
  },
  {
    slug: 'maintenance',
    title: 'Maintenance & planned windows',
    icon: 'pi pi-wrench',
    summary: 'Toggling maintenance and declaring planned windows.',
    markdown: `# Maintenance & planned windows

Maintenance has two screens: **toggles** (the live switches) and **planned events** (windows you schedule ahead of time).

## Toggles

There is one switch per audience, per environment:

- **Consumers** — B2C users.
- **Operators** — business / operator admins.
- **Community members** — community users.

Each row shows the current state and who changed it last. Flipping a switch asks for confirmation (naming the environment, audience, and direction); in production it also requires the typed \`prd\` confirmation.

> **Maintenance flags are always flipped by hand.** Planned events never flip them for you — they only announce.

**Honesty note:** a new customer session sees a flag change **immediately**; an already-active session picks it up **within about 60 minutes** (the app re-checks on that cadence).

## Planned events

A planned event **declares** a maintenance window and drives the customer-facing banner and notification emails. It does **not** turn maintenance on — you still flip the toggle yourself when the time comes.

When you create one you choose:

- The **audience** and the **window** (start and end).
- **Declare lead time** — how long before the start the banner appears.
- **Notify lead times** — when (if at all) notification emails go out. A second notification requires a first.

The form shows a plain-language timeline preview ("Banner appears 24h before start", and so on). Events are **Draft** (inert) until you make them **Active**; the dispatcher then surfaces the banner, sends the emails once each, and marks the event **Completed** after its window ends. You can extend an active window's end time or cancel it.`,
  },
  {
    slug: 'promotions',
    title: 'Coupons & campaigns',
    icon: 'pi pi-tag',
    summary: 'Building coupons, using the simulator, and linking campaigns.',
    markdown: `# Coupons & campaigns

## The coupon list

A searchable, decoded table of every coupon: its code, target, type, item, validity, usage (used / allowed), linked campaign, and lifecycle status. Open a row to edit it, or use **New coupon** to launch the wizard.

## The coupon wizard

Four steps: **Basics → Eligibility → Discount → Review**.

- A **plain-language preview** of what the coupon does is visible the whole time and updates as you type.
- A **live rule checklist** shows which rules currently pass or fail, with an explanation for each — invalid combinations cannot be saved, and the server re-checks the same rules on save.
- The **value caption** changes with the item and discount type so the number you enter is never ambiguous (a percentage vs. a fixed amount, and what it applies to).

### The simulator

The **Review** step embeds a simulator. Enter a realistic B2C or B2B scenario and it returns the **original, discount, and final** amounts plus a month-by-month schedule showing exactly when the discount applies. The maths follows the platform's documented pricing patterns.

## After redemption

Once a coupon has been used, most fields **freeze**. You can still extend its expiry, increase the allowed number of uses, or deactivate it. **Deactivating expires a coupon now — it is never deleted**, so its history stays intact.

## Campaigns

Campaigns group promotional messaging and can link to a coupon. When you pick a coupon its validity is shown as a badge, and you are warned if the linked coupon has expired.`,
  },
  {
    slug: 'exceptions',
    title: 'Exceptions dashboard',
    icon: 'pi pi-exclamation-triangle',
    summary: 'The nine operational checks and how to drill in.',
    markdown: `# Exceptions dashboard

The Exceptions board is your home base: nine tiles, each a standing check for a class of problem that needs a human. It is scoped to the **selected environment** and refreshes itself every few minutes while open.

## The tiles

- **Overdue invoices** — money billed but not collected.
- **Cancelled invoices** — monthly invoices cancelled in PayPal for a business that is still Active and whose period has not been re-billed. MDT never leaves an invoice cancelled itself, so each row is a manual PayPal action that needs a decision: (1) the customer signalling exit outside the app's termination flow, (2) a deliberate correction — the next monthly invoice re-bills the period and the row clears by itself, or (3) an orphan of a community/business being terminated — it clears once the business leaves Active. The *Inactive communities* column helps tell (3) apart. Nothing is terminated automatically; click the business to open its Customer 360. You are also emailed when the cancellation arrives.
- **Lost-debt invoices** — invoices in *Lost debt* status: never cleared and written off (e.g. a business terminated by the daily process for unpaid invoices). They leave the *Overdue invoices* tile once written off, so this tile keeps the money-owed visible. Warning normally; critical while the write-off is under 7 days old. It does not self-clear — the row stays until the invoice leaves that status. Click the business to open its Customer 360.
- **Businesses winding down** — accounts in downgrade / freeze / termination, with their lifecycle clocks.
- **Communities winding down** — communities paused or winding down, with pause-cap milestones.
- **Unpaid subscriptions** — users whose billing is retrying or has been denied.
- **Stuck pending tasks** — scheduled tasks the daily batch has not processed.
- **Error logs** — error-severity events in the recent window.
- **Stuck ingestions** — document versions stuck retrying or permanently failed.
- **Commission payouts due** — operator balances at or above the payout threshold.

## Colours and ages

Each tile shows a **count**, a **severity colour** (clear / needs attention / critical), and the **age of the oldest item**. When everything is clear you get a green "Nothing needs you in {environment}".

## Drilling in

Click any tile with a count to open a **drill-down table** — server-paginated, urgency-sorted, every code decoded. Each row leads with the entity it refers to (a plain reference in Phase A; it becomes a Customer-360 link later).

## Automatic alerts

A daily evaluation emails you when the compounding-risk check (overdue invoices) has items. Thresholds are configurable under [Settings](/help/settings).`,
  },
  {
    slug: 'audit',
    title: 'Audit trail',
    icon: 'pi pi-history',
    summary: 'Finding who changed what, where, and when.',
    markdown: `# Audit trail

Every change made in MDTA is recorded — permanently and append-only. The Audit browser is how you read that record. It spans **all environments** (the environment is shown as a column, not chosen from the top bar).

## Filtering

Narrow the trail by **admin**, **environment**, **action**, **entity**, and a **date range**. Actions and entities are chosen from decoded lists, so you filter by meaning rather than by raw code. Click **Apply** to run the filter and **Clear** to reset.

## Reading a row

Each row shows **when** it happened, **who** did it (name and email), the **environment**, the **action**, the **entity** it affected, and the originating **IP**.

Rows that changed data can be **expanded** to show a **before / after** comparison of the underlying record — the exact state prior to the change on the left, and the resulting state on the right.

> Sign-in, sign-out, and configuration changes are environment-agnostic and appear with the **---** environment marker.`,
  },
  {
    slug: 'settings',
    title: 'Settings',
    icon: 'pi pi-cog',
    summary: 'Global configuration and where each value is used.',
    markdown: `# Settings

Settings edits the platform's global configuration. These values are **global** — they are not tied to an environment, so there is no production confirmation here. Each key shows its own description; save changes **one key at a time**, and every save is audited.

## Groups

- **Alerts** — up to three recipient email addresses for health and exception alerts (all are notified in parallel), and the still-down reminder interval.
- **Authentication & security** — whether MFA is required for all admins, and the failed-login count and lockout duration.
- **Probes & monitoring** — how often the two tiers of health probes run, and how long probe history is kept.
- **Exception thresholds** — the day/hour cut-offs that decide when an item shows up on the Exceptions board (overdue invoices, stuck pending tasks, stuck ingestions, and the error-log window).

## Good to know

- Changing a **probe cadence** takes effect after the service restarts.
- Alert emails are validated before they are accepted; you can hold at most three.
- Admin management (creating, disabling, and resetting admins) lives here too, behind its own permission.`,
  },
  {
    slug: 'customer-360',
    title: 'Customer 360',
    icon: 'pi pi-id-card',
    summary: 'Search any customer entity and read its full profile.',
    markdown: `# Customer 360

One search box finds any **business**, **user**, or **community** in the selected environment — by name, email, subdomain, user hash, and (for a numeric query) by **id across all three**. A result opens the entity's full profile. The tabs are read-only; the deliberate **write actions** (status changes, email resends) live behind the **Actions** control and each carry their own confirmation — see [Actions & lifecycle guardrails](/help/actions).

## Profiles & tabs

Each profile loads **one tab at a time** (fast first paint) and caches what you've opened. Every status renders decoded with an explanation tooltip — there are no naked codes.

- **Business 360** — overview (billing model, tax profile, caps, the Phase-C Health-Score placeholder), contacts, the communities→teams→advisors tree, engagement trends, knowledge (RAG documents, usage vs. allocation, top-used chunks), financial (invoices, purchases, commissions incl. payout receipts, accounting docs, coupon schedule, data exports), a filterable transactions browser, pending lifecycle tasks, and feedback & event history.
- **User 360** — overview, engagement vs. plan limits, teams, billing, conversations, communications (email preferences and blocked emails), and a timeline of the user's logged events.
- **Community 360** — overview (with pause/wind-down clocks), members vs. cap, teams & advisors, engagement, knowledge, email-preference distribution, and pending tasks.

## The status scenario sentence

A user's account state is governed by three codes together (status × billing status × permission group). When the combination matches a documented lifecycle scenario, the overview renders it as **one plain-language sentence** (e.g. *"Downgraded to free tier — manually turned auto-renewal off"*). Undocumented combinations show the three decoded badges only — MDTA never invents a sentence.

## Chat content & privacy

The Conversations tab lists **metadata only** (dates, team, rating, sizes). Opening a conversation's **message content** requires an explicit confirmation, and **every content view is recorded in the audit trail** — who viewed which conversation, and when. That record is the provable answer to "did you read my chat?": only when you asked for support, and here's the log.

## Health score

Business 360 (and User 360) open with a **health score** — a single 0–100 number in a colour band (red / amber / green), **computed on demand** from bounded queries over the selected environment.

A score you can't explain is a score you can't act on, so the **breakdown is not optional**: expand it to see every factor with its raw value, its normalized 0–10, its weight, and its contribution. Factors cover signups pace, engagement volume and trend, revenue, feedback, RAG adoption, billing history, rating usage, plan tier, and seniority (the user set swaps in premium-team membership). Each factor's definition is in its row tooltip.

The **weights** live in **Settings → Health score** and take effect on the next computation — no deployment. A factor with no meaningful value for an entity (e.g. plan tier for a Company-Pays user) is **excluded** and the remaining weights renormalize, so the score stays honest.

### Trend (Business 360)

On a **Business 360**, the health tile also has a **Trend** expander: a monthly **snapshot** of the business score, taken automatically on the 1st of each month, shown as a small line chart with the per-point factor breakdown. A health score is a present-tense reading, so there is **no backfill** — the trend simply starts accruing the month this phase deploys, and the tile says "history begins {month}" honestly. It needs two monthly snapshots before it can draw a line.

> Profiles are environment-specific. Switching environment from a profile returns you to the search — an id means a different record in another environment.`,
  },
  {
    slug: 'catalog',
    title: 'B2C catalog analytics',
    icon: 'pi pi-th-large',
    summary: 'How the pre-defined B2C team shelf performs.',
    markdown: `# B2C catalog analytics

The B2C Teams page is MDT's own product shelf. This screen shows how each pre-defined team performs, so promote / retire / convert-to-Premium decisions are data-driven.

## The table

One row per catalog team, sortable by any column, over the selected **time range** (presets 30 / 90 / 365 days / All time, plus a custom range; the default is the last 90 days):

- **Assignments** — how many users hold the team, with the premium/free mix.
- **Premium conversion** — the share of assignments that are Premium: the best signal for which free teams to market as Premium.
- **Conversations & activity share** — actual usage in range, and the team's slice of all catalog activity: popularity vs. dead weight at a glance.
- **Rating** — the average chat rating **with its volume beside it**. Averages built on fewer than 30 ratings are shown muted: an average of 2 ratings is noise, of 200 is signal.

## Per-team drill-down

Click a row for the team's monthly trends (conversations, new assignments), its **rating distribution**, and **advisor participation** — each advisor persona's share of the messages contributed in the team's conversations, which tells you who is vocal and who is rarely heard when tuning personas.

> Advisor participation groups messages by the display name recorded at send time (consistent with the conversation's team snapshot), so a renamed advisor appears under both names.`,
  },
  {
    slug: 'browsers',
    title: 'Feedback & log browsers',
    icon: 'pi pi-list-check',
    summary: 'Filterable browsing of user feedback and the application event log.',
    markdown: `# Feedback & log browsers

Two cross-cutting tables that previously required the DB console, now filterable and paginated in the selected environment.

## Feedback

Everything users wrote in, newest first. Filter by **date range**, **free text** over the message, or a **business / community id** (resolved through the submitting user). Long messages expand in place, and the submitter links straight into their **User 360** profile.

## Logs

The MDT application event log with every code decoded. Filters combine freely:

- **Category** and **severity** — decoded dropdowns.
- **Event** — searchable **by name**, not just by number (start typing "invoice" or "coupon").
- **User id / source id** — the two entity pointers a log row carries.
- **Payload text** and a **date range**.

Rows with a data payload expand to a pretty-printed view. The current filters live in the page URL, so a filtered view can be bookmarked or shared.

## Deep links

Other screens land here pre-filtered: the Exceptions board's **error-logs** drill-down links each error group to its matching rows, the **User 360 timeline** is this table fixed to one user, and the **Business 360 history** is this table fixed to one business.`,
  },
  {
    slug: 'actions',
    title: 'Actions & lifecycle guardrails',
    icon: 'pi pi-bolt',
    summary: 'Which status changes MDTA offers, and why the rest are blocked.',
    markdown: `# Actions & lifecycle guardrails

MDTA offers **only administrative transitions that have no process machinery behind them**. Anything owned by a lifecycle process — wind-downs, downgrades, terminations, purges, all driven by pending tasks and the daily batch — is *shown* with its clock but **blocked**, with an explanation naming the owning flow. MDTA never forks a second path into a regulated process.

Open the **Actions** control on a profile (or a team / document-version row) to see what is legal *right now*. Available transitions are clickable; blocked ones are greyed with a tooltip saying why. The list comes straight from the server, so the app can never offer something the API would refuse.

Every transition is **audited** with before/after, uses an **optimistic guard** (if the record changed since you loaded it you get a "reload and retry" rather than a silent overwrite), and in **production** requires the typed confirmation.

## Users

| From → To | Meaning |
|---|---|
| Inactivated → Activated | Fix a stuck inactivated account |
| Activated → Inactivated | User loses access at the next guard check |
| Activated/Inactivated → Banned | **Ban (system)** — reason required; also logged platform-side (event 230) |
| Banned → Activated | **Unban** — reason required; also logged platform-side (event 231) |

Blocked: password-reset, onboarding, invited, ban-by-business, deletion, and downgrade flags — each owned by its own flow.

## Businesses

Inactivate ↔ Reactivate, and **Restore from Default** (reason required). The restore dialog shows the business's **overdue invoices** to certify as settled, and lists the **child communities the system inactivated** with checkboxes to restore them in the same act. Wind-down states (Downgrading / Invoice-Freeze / Terminating / Terminated) are blocked — they belong to the lifecycle process.

## Communities

Only the documented **recovery** paths — there is no direct activate/deactivate flip, because deactivation and pause *are* regulated processes:

- **Resume paused** — also cancels the two pause tasks (pause-cap warning + auto-deactivation).
- **Cancel deactivation** — Company-Pays only; User-Pays wind-down is irreversible and stays blocked.
- **Restore community inactivated by system** — reason required, appended to the community's comments; the dialog shows the parent business's status so restoring under a still-defaulted parent is a conscious act.

## Teams & document versions

Teams toggle active ↔ inactive (teams inside paused/wind-down communities stay inherited-inactive regardless). Document versions can be **re-queued** (Retry/Error → Uploaded, so the ingestion retry picks them up) or **discarded** — closing the loop on the *stuck-ingestions* exception, whose drill-down offers the same action inline.`,
  },
  {
    slug: 'commissions',
    title: 'Commissions',
    icon: 'pi pi-percentage',
    summary: 'Operator revenue-share balances, payouts, and receipt tracking.',
    markdown: `# Commissions

The commission surface is **read + receipt operations only**. Payout records are created **exclusively by the automated quarterly machinery** — MDTA never initiates a payout and never calls PayPal. If the automated run misfires, that is an incident to diagnose in the daily jobs, not to paper over from here.

## How the payout machinery works

Each business accrues an operator revenue share in a rolling **commission ledger**. Quarterly, the system creates a **payout** for balances due, submits it to PayPal in a daily batch, and **polls** for the transaction result (success / pending / unclaimed / returned / failed) over subsequent runs. MDTA reads all of that; it does not drive it.

## Balances tab

The latest ledger balance per business, with a **Due** badge (at/above the payout threshold) and an **In flight** badge (a payout is created/pending/unclaimed). Business links jump to the Business 360 financial tab.

## Payouts tab

Every payout, filterable by status, business, and an **awaiting-receipt** toggle (successful payouts with no receipt recorded yet). Expand a row for the PayPal ids, transaction status, and any error reason. Two actions on a successful payout:

- **Mark receipt received** — records \`invReceiptReceived\` and an optional receipt number (the only write on the payout).
- **Send receipt reminder** — the **M-3** email (template code 55) to the business's Billing/Admin contacts, asking them to upload the receipt. You preview the exact recipients and values before it sends.`,
  },
  {
    slug: 'reference-data',
    title: 'Reference data',
    icon: 'pi pi-book',
    summary: 'Taarif tier ladders and country/state tax rates.',
    markdown: `# Reference data

Two financially-sensitive lookup tables, editable per environment. **Only platform-admins can change reference data**; finance can read it.

> **Future calculations only.** Changes affect the **next** monthly invoice run / payout — already-issued invoices and documents are never touched. When you change these, remember to update the corresponding help pages in MDT.

## Taarif — tiered pricing

Cost brackets per **(cost category × product × metric)**, shown as a **ladder** so gaps and overlaps are visible at a glance. Cost categories: maintenance fee, user-management-module cost, included voice minutes, and business commission percent. Metric is USERS or MINUTES.

Validation is enforced in the UI **and** the API: cost category 1–4, a bracket threshold **unique within its ladder** (duplicates are refused), a non-negative value, and — for the commission-percent category — a value no greater than 100.

## CountriesTax — tax rates

Tax rate rows by country (2-letter ISO) and optional state. This table feeds **invoice tax computation** and **payout VAT** (the Israel rule). Validation: uppercase ISO country, a unique country/state pair, a tax name, and a percent between 0 and 100.

Every change is audited with the full before/after row, and production edits go through the typed confirmation.`,
  },
  {
    slug: 'roles-admins',
    title: 'Roles & admins',
    icon: 'pi pi-users',
    summary: 'Role capability sets and managing back-office accounts.',
    markdown: `# Roles & admins

Back-office access is capability-based. A **role** is a default set of capabilities; a per-admin **override** can replace it. Manage all of this in **Settings → Admins**.

## Roles

- **Platform admin** — every capability, including maintenance, coupons, settings, reference-data writes, and admin management.
- **Support** — read the 360 profiles (including audited chat-content views), feedback, logs, catalog, exceptions and health, and resend transactional emails. No maintenance, coupons, settings, or admin management.
- **Finance** — the money surfaces: 360 financial/billing tabs, exceptions, commissions (read + receipt ops), reference-data **read**, and the audit trail. Finance **cannot** edit reference data.

Adding a colleague is *create admin, assign role* — no code.

## Managing admins

Create, edit (name / role / capability override), disable/enable, and send a password-reset link. Notes:

- A **role or capability change** takes effect on the target's **next token refresh** (within ~15 minutes).
- **Disabling** an admin revokes their sessions immediately.
- You **cannot** disable, demote, or reset **yourself**, and the **last active platform-admin** cannot be disabled or demoted — so no one can lock everyone out.

## Password reset

Instead of a manager typing a new password, MDTA emails the admin a **single-use reset link** (valid 30 minutes). Completing it sets the new password and **revokes all existing sessions**; if the link was sent with the TOTP-reset option, it also clears authenticator enrolment so the admin can re-enrol. The reset page is public (behind the sign-in rate limiter) — it needs only the token.

## The management role

**Management** is a read-only analytics role: it sees **only** the Analytics area (trends, cohorts, export) and nothing else — no 360 profiles, no browsers, no writes, and it cannot run stats collection. Give it to stakeholders who need the numbers but no operational surface. Catalog analytics is a separate capability and is **not** included by default (add it as a per-admin override if needed).`,
  },
  {
    slug: 'analytics',
    title: 'Analytics',
    icon: 'pi pi-chart-bar',
    summary: 'KPI trends, the Explorer, cohorts, CSV export, and how stats are collected.',
    markdown: `# Analytics

Analytics is the platform's **monthly KPI** surface. It reads the Stats table — one value per business / community / team, per month, per KPI code — and presents it as trends, breakdowns, cohorts and a CSV export. It is read-dominant: the only action is running the monthly collection itself.

## What Stats is

- **Monthly and closed-month.** A month is collected only once it has ended. There is no intra-month or real-time analytics here — live operational numbers live on Exceptions, Catalog and the 360 tabs.
- **Four grains**, decoded on every code: **vertical** (whole Biz/Fans vertical), **business**, **community**, and **team** (chat KPIs — every chat belongs to a team).
- **Money is per currency.** Money KPIs are never summed across currencies; each currency is its own line on the chart and its own row in the export.

## Measured vs. calculated codes

- **Measured (Q)** codes are collected by a SQL query each month and stored.
- **Calculated (C)** codes are **computed on read** from other codes via a formula registry — they are never stored, so a formula fix retroactively corrects all history.

## Roll-up honesty

- **Sum** codes (counts, sums, money) add up over the scope.
- **Average / percent** codes are rolled up as an **unweighted mean** of the entity-level values. When you view such a code above its native grain, a **caveat pill** says so — we do not fake a conversation-weighted mean we cannot compute from Stats.
- A division by zero renders as a **gap** in the chart, never as 0.

## The three screens

- **Overview** — one card per curated KPI (analytics.overviewCodes in Settings): latest value, a 12-month sparkline, and the change vs. the prior month. Click a card to open it in the Explorer.
- **KPI Explorer** — pick up to four codes, a scope (env-wide → business → community → team) and a month range. Get a chart (line/bar), a numeric table, a **CSV export**, and — click any month point — a **breakdown** of that month by business / community / team that links straight into the 360 profiles.
- **Cohorts** — signup-cohort engagement curves (free vs. paying): average all-time conversations per user by months since signup. These averages **include zero-conversation users** and count up to month end — read them as retention, not raw activity.

## Export

The CSV is the **raw stored rows** (measured codes only — calculated codes are derivable and excluded), UTF-8 with a BOM so Excel opens it cleanly. Very large requests are refused with a clear message asking for a narrower range or scope.

## Collection & backfill

Collection runs **monthly on a schedule** (per environment, UTC) and writes each run's outcome to the collection-status strip on the Overview and a platform-side log entry. A platform admin can also, from the Overview:

- **Collect now** — collect the most recently closed month.
- **Backfill month…** — collect an earlier closed month. Backfill runs **window codes only**; snapshot codes (accumulated headcounts, balances, "at month end" values) are **skipped** — an accumulated value cannot be truthfully reconstructed for the past. The dialog states this up front and the result lists what was skipped.

Re-collecting a month is safe: it replaces the same rows, so numbers stay stable across re-runs. Collection writes straight into Stats from admin-api and the CSV export is the spreadsheet path (the old Google Sheet is retired).

See also the **Health-score trend** on a Business 360 (a monthly snapshot that starts accruing when this phase deploys), described under [Customer 360](/help/customer-360).`,
  },
];

export const HELP_BY_SLUG: Record<string, HelpTopic> = Object.fromEntries(
  HELP_TOPICS.map((t) => [t.slug, t])
);

// Map a top-level app route to its help topic slug (used by the shell's "?").
export function helpSlugForUrl(url: string): string {
  const path = url.split('?')[0].split('#')[0];
  const seg = path.split('/').filter(Boolean)[0] ?? '';
  switch (seg) {
    case 'exceptions':
      return 'exceptions';
    case 'health':
      return 'health';
    case 'maintenance':
      return 'maintenance';
    case 'promotions':
      return 'promotions';
    case 'audit':
      return 'audit';
    case 'settings':
      return 'settings';
    case 'c360':
      return 'customer-360';
    case 'catalog':
      return 'catalog';
    case 'browsers':
      return 'browsers';
    case 'commissions':
      return 'commissions';
    case 'reference-data':
      return 'reference-data';
    case 'analytics':
      return 'analytics';
    case 'account':
      return 'getting-started';
    default:
      return 'getting-started';
  }
}
